import { useNextRouter } from "@/data/Content/_NextRouter";
import { getContractIdParamFromContext, getSettings, useSettings } from "@/data/Settings";
import { getUser, useUser } from "@/data/User";
import { getClientSideCommon } from "@/data/utils/getClientSideCommon";
import { expand, shrink } from "@/data/utils/keyUtil";
import { queryCategoryViewResource } from "integration/generated/query";
import { CatalogGroupDetailArray } from "integration/generated/query/data-contracts";
import { RequestParams } from "integration/generated/transactions/http-client";
import { GetServerSidePropsContext } from "next";
import { error as logError } from '@/data/utils/loggerUtil';
import useSWR from "swr";
import { ContentProps } from "@/data/types/ContentProps";
import { getServerSideCommon } from "@/data/utils/getServerSideCommon";
import { getServerCacheScope } from "@/data/cache/getServerCacheScope";
import { unstable_serialize as unstableSerialize } from 'swr';

const DATA_KEY = "Categories_By_Ids";

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
		async (
			query: {
				id: string[];
				[key: string]: string | boolean | (string | number)[];
			},
            storeId: string,
			params: RequestParams
		): Promise<CatalogGroupDetailArray | undefined> => {
			try {
                const data = await queryCategoryViewResource(pub).findCategoriesByUniqueIds(
                    storeId,
                    query,
                    params
                );
                return data;
            } catch (error) {
                logError(context?.req, 'StoreLocator: fetcher: error: %o', error);
            }
		};

export const getCategories = async ({cache,id,context,}: ContentProps) => {
	if(!id || id?.toString()?.trim() === "" || !Number.isFinite(Number(id)))return; //id will be String value for search page and this api call not needed in that case
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const { storeId, defaultCatalogId: catalogId, langId , defaultCurrency:currency} = getServerSideCommon(settings, context);
	const props = {
		storeId,
		id:[id?.toString()],
		catalogId,
		langId,
		currency,
		...getContractIdParamFromContext(user?.context),
	};
	const cacheScope = getServerCacheScope(context, user.context);
	const key = unstableSerialize([shrink(props), DATA_KEY]);
	const value = cache.get(key, cacheScope) || await fetcher(false, context)(props,storeId, {});
	cache.set(key, value, cacheScope);
	return Promise.resolve(value);
}

export const useCategories = (id:string[]) => {
	id = id?.filter(id => id?.trim() !== "" && Number.isFinite(Number(id))); //id will be String value for search page and needs to be fitered.
    const router = useNextRouter();
	const { settings } = useSettings();
	const { user } = useUser();
	const currentContract = getContractIdParamFromContext(user?.context);
	const contract = currentContract;
	const {
		storeId,
		defaultCatalogId: catalogId,
		defaultCurrency: currency,
		langId,
	} = getClientSideCommon(settings, router);
	
    const { data, error, isLoading } = useSWR(
		storeId && id?.length>0
			? [
					shrink({
						storeId,
						id,
						catalogId,
						langId,
						currency,
						...contract,
					}),
					DATA_KEY,
			  ]
			: null,
		async ([props]) => fetcher(true)(expand(props), storeId, {})
	);
	return {
		catGrpDetails: data?.catalogGroupView??[],
		loading: id && !error && isLoading,
		error,
	};
}